import { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { getExpressContacts } from "../services/expressMaster";
import {
  ExpressContact,
  ExpressContactListParams,
  ExpressMasterList,
} from "../types/express-master.type";
import { useDebounce } from "../utils/hooks/useDebounce";
import { ALL } from "../components/CreditDebitNote/constants";
import MasterFilter, {
  EMPTY_MASTER_FILTER,
  MasterFilterValue,
} from "../components/ExpressMaster/MasterFilter";
import MasterTable, { MasterColumn } from "../components/ExpressMaster/MasterTable";
import ContactDialog from "../components/ExpressMaster/ContactDialog";
import { useMasterList } from "../components/ExpressMaster/useMasterList";
import {
  CONTACT_KIND_LABEL,
  EXPRESS_CONTACT_MENU_TITLE,
  isActiveParam,
} from "../components/ExpressMaster/constants";

const SEARCH_DEBOUNCE_MS = 500;

const fetchContacts = async (query: ExpressContactListParams): Promise<ExpressMasterList<ExpressContact>> => {
  const {
    data: { data },
  } = await getExpressContacts(query);
  return data ?? { rows: [], count: 0 };
};

const CONTACT_COLUMNS: MasterColumn<ExpressContact>[] = [
  { key: "code", label: "รหัส", render: (contact) => contact.code },
  { key: "kind", label: "ประเภท", render: (contact) => CONTACT_KIND_LABEL[contact.kind] },
  { key: "title", label: "คำนำหน้า", render: (contact) => contact.title ?? "-" },
  { key: "name", label: "ชื่อ", render: (contact) => contact.name },
  { key: "tax_id", label: "เลขประจำตัวผู้เสียภาษี", render: (contact) => contact.tax_id ?? "-" },
  { key: "tax_branch_no", label: "สาขา#", align: "right", render: (contact) => contact.tax_branch_no },
  { key: "phone", label: "โทร.", render: (contact) => contact.phone ?? "-" },
  { key: "credit_days", label: "เครดิต (วัน)", align: "right", render: (contact) => contact.credit_days },
];

/** ผู้จำหน่าย / ลูกค้า (Express) — head office keeps the suppliers and pseudo customers. */
export default function ExpressContactPage() {
  const [filter, setFilter] = useState<MasterFilterValue>(EMPTY_MASTER_FILTER);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [editingContact, setEditingContact] = useState<ExpressContact | null>(null);
  const debouncedSearch = useDebounce(filter.search, SEARCH_DEBOUNCE_MS);
  const { status, kind } = filter;
  const filters = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      is_active: isActiveParam(status),
      kind: kind === ALL ? undefined : kind,
    }),
    [debouncedSearch, status, kind],
  );
  const list = useMasterList(fetchContacts, filters);
  const { reload } = list;

  const handleFilterChange = (patch: Partial<MasterFilterValue>) =>
    setFilter((previous) => ({ ...previous, ...patch }));
  const handleAdd = () => {
    setEditingContact(null);
    setIsDialogOpen(true);
  };
  const handleEdit = (contact: ExpressContact) => {
    setEditingContact(contact);
    setIsDialogOpen(true);
  };
  const handleCloseDialog = () => setIsDialogOpen(false);
  const handleSaved = () => {
    setIsDialogOpen(false);
    reload();
  };

  return (
    <Stack sx={{ padding: "8px 0" }} gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Typography variant="h6" fontWeight={700}>
          {EXPRESS_CONTACT_MENU_TITLE}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          เพิ่มผู้จำหน่าย / ลูกค้า
        </Button>
      </Stack>
      <Paper sx={{ padding: "24px 16px" }}>
        <MasterFilter value={filter} hasKind onChange={handleFilterChange} />
      </Paper>
      <MasterTable columns={CONTACT_COLUMNS} list={list} onEdit={handleEdit} />
      <ContactDialog
        open={isDialogOpen}
        contact={editingContact}
        onClose={handleCloseDialog}
        onSaved={handleSaved}
      />
    </Stack>
  );
}
